// =============================================================================
// Legacy → new-slice projection helpers.
//
// Pure functions. Used at store creation time (Phase 2) to seed state.auth
// and state.profile.data synchronously from the existing localStorage.userInfo
// blob, BEFORE React mounts. This guarantees:
//
//   - axios request interceptor never observes empty auth on cold start
//     (it still reads localStorage today, but selectAccessToken / future
//      consumers reading state.auth see real values from tick 0).
//   - wsManager.connect on mount sees the same access as legacy reads do.
//   - selectAuth / selectProfile return populated values immediately on
//     cold-start hydration, well before bootstrapApp() refreshes from server.
//
// The legacy localStorage contract (single 'userInfo' key, blob shape) is NOT
// changed by Phase 2. Persistence stays exactly as it was; we only project
// the same source into additional read shapes.
//
// Phase 6 will add the inverse direction (projectLegacyUserInfo) in this same
// file for the read-only legacy mirror.
// =============================================================================

export function projectAuthFromLegacy(blob) {
  if (!blob) {
    return {
      access: null,
      refresh: null,
      status: 'idle',
      error: null,
      userId: null,
      publicId: null,
      email: null,
      isEmailVerified: false,
    };
  }

  return {
    access: blob.access ?? null,
    refresh: blob.refresh ?? null,
    status: 'idle',
    error: null,
    userId: blob.id ?? null,
    publicId: blob.public_id ?? null,
    email: blob.email ?? null,
    isEmailVerified: blob.is_email_verified ?? false,
  };
}

export function projectProfileFromLegacy(blob) {
  if (!blob) {
    return {
      data: null,
      status: 'idle',
      error: null,
      hydration: 'unauthenticated',
    };
  }

  // Strip auth-only keys; keep everything else as the profile blob. The shape
  // here intentionally mirrors today's userInfo (snake_case fields) so the
  // future Phase 5 consumers can read profile.data with the same field names
  // they already use against userInfo. Phase 6 may normalise further.
  //
  // CONTRACT: the value seeded here is OPTIMISTIC STALE CACHE — see the
  // stale-cache contract in profileReducers.js. Treat as authoritative only
  // after hydration flips to 'success'. Until then, this is the same shape
  // a refresh-stale-page would observe.
  const { access, refresh, ...profileFields } = blob;

  return {
    data: profileFields,
    status: 'idle',
    error: null,
    // 'pending' because bootstrapApp() will refresh from server on mount.
    hydration: 'pending',
  };
}
